import { useAtom } from 'jotai';
import { useState, useCallback } from 'react';
import { productImagesAtom } from '~/atoms/productImages';
import { fetchProductsByModelIds } from '~/service/productsService';
import { fetchProductImage } from '~/service/productImagesService';
import log from '~/logger';
function createLimiter(concurrency) {
    let active = 0;
    const queue = [];
    const runNext = () => {
        if (active >= concurrency)
            return;
        const next = queue.shift();
        if (!next)
            return;
        active += 1;
        next();
    };
    return function limit(fn) {
        return new Promise((resolve, reject) => {
            const task = () => {
                fn()
                    .then(resolve, reject)
                    .finally(() => {
                    active -= 1;
                    runNext();
                });
            };
            queue.push(task);
            runNext();
        });
    };
}
export function useProductImages() {
    const [productImages, setProductImages] = useAtom(productImagesAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const fetchImagesForModels = useCallback(async (modelIds) => {
        if (modelIds.length === 0) {
            return;
        }
        const uniqueModelIds = Array.from(new Set(modelIds));
        const modelIdsToFetch = uniqueModelIds.filter((id) => !productImages.has(id.toString()));
        if (modelIdsToFetch.length === 0) {
            log.info('All requested models already cached', {
                modelIds: uniqueModelIds
            });
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const products = await fetchProductsByModelIds(modelIdsToFetch);
            if (products.length === 0) {
                const newImages = new Map(productImages);
                modelIdsToFetch.forEach((modelId) => {
                    newImages.set(modelId.toString(), []);
                });
                setProductImages(newImages);
                setIsLoading(false);
                return;
            }
            setIsLoading(false);
            const limit = createLimiter(20);
            const productIdsSet = new Set(products.map((p) => p.id.toString()));
            products.forEach((product) => {
                limit(() => fetchProductImage(product.id))
                    .then((productImageData) => {
                    setProductImages((currentImages) => {
                        const newImages = new Map(currentImages);
                        const existingImages = newImages.get(product.modelId.toString()) || [];
                        if (productImageData && productImageData.images.length > 0) {
                            newImages.set(product.modelId.toString(), [
                                ...existingImages,
                                ...productImageData.images
                            ]);
                        }
                        return newImages;
                    });
                })
                    .catch((err) => {
                    log.error('Failed to fetch image for product', {
                        productId: product.id,
                        modelId: product.modelId,
                        error: err instanceof Error ? err.message : 'Unknown error'
                    });
                })
                    .finally(() => {
                    productIdsSet.delete(product.id.toString());
                    setProductImages((currentImages) => {
                        const newImages = new Map(currentImages);
                        let hasChanges = false;
                        modelIdsToFetch.forEach((modelId) => {
                            if (!newImages.has(modelId.toString())) {
                                const modelProducts = products.filter((p) => p.modelId === modelId);
                                const allProductsCompleted = modelProducts.every((p) => !productIdsSet.has(p.id.toString()));
                                if (allProductsCompleted) {
                                    newImages.set(modelId.toString(), []);
                                    hasChanges = true;
                                }
                            }
                        });
                        return hasChanges ? newImages : currentImages;
                    });
                });
            });
        }
        catch (err) {
            const errorObj = err instanceof Error ? err : new Error('Unknown error');
            setError(errorObj);
            log.error('Failed to fetch images for models', {
                error: errorObj.message,
                modelIds: modelIdsToFetch
            });
            const newImages = new Map(productImages);
            modelIdsToFetch.forEach((modelId) => {
                newImages.set(modelId.toString(), []);
            });
            setProductImages(newImages);
        }
        finally {
            setIsLoading(false);
        }
    }, [productImages, setProductImages]);
    const getImagesForModel = useCallback((modelId) => {
        return productImages.get(modelId) || [];
    }, [productImages]);
    return {
        fetchImagesForModels,
        getImagesForModel,
        isLoading,
        error
    };
}
